import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Trophy } from "lucide-react";

function safeFormat(value: unknown, fmt = "MMM d"): string {
  if (value == null || value === "") return "—";
  try {
    const d = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(d.getTime())) return "—";
    return format(d, fmt);
  } catch {
    return "—";
  }
}
import type { RecentWin } from "@/api/analytics";

interface RecentWinsProps {
  wins?: RecentWin[];
  projectSlug: string;
  loading?: boolean;
}

/** Compact list of recently-won deals — each row links back to the
 *  company detail page. Empty-state copy is intentionally
 *  encouraging ("No deals won yet — keep pushing!"). */
export function RecentWins({ wins, projectSlug, loading }: RecentWinsProps) {
  if (loading) {
    return (
      <div className="rounded-plane bg-white p-5 shadow-plane">
        <div className="mb-3 h-3 w-24 animate-pulse rounded bg-slate-100" />
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-9 animate-pulse rounded-lg bg-slate-100"
            />
          ))}
        </div>
      </div>
    );
  }
  return (
    <section className="rounded-plane bg-white p-5 shadow-plane">
      <h3 className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        <Trophy className="h-3.5 w-3.5 text-amber-500" />
        Recent wins
      </h3>
      {!wins || wins.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
          <span className="text-2xl" aria-hidden>
            🎯
          </span>
          <p className="text-sm font-medium text-slate-700">
            No deals won in this period.
          </p>
          <p className="text-xs text-slate-400">Keep pushing!</p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {wins.map((win, idx) => (
            <li
              key={`${win.company_id ?? "x"}-${win.won_at ?? "y"}-${idx}`}
            >
              <Link
                to={`/projects/${projectSlug}/companies/${win.company_id}`}
                className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-sm transition-colors duration-150 hover:bg-indigo-500/[0.04]"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span aria-hidden>🏆</span>
                  <span className="truncate font-medium text-slate-800">
                    {win.company_name}
                  </span>
                  {win.sector && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      {win.sector}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
                  <span>won {safeFormat(win.won_at)}</span>
                  {win.owner_name && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="truncate text-slate-600">
                        {win.owner_name}
                      </span>
                    </>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
